import Dropzone, { useDropzone } from "react-dropzone";
import { IDropZone } from "./UploadDropZoneInterfaces";
import { useStyles } from "./UploadDropZone.styles";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { formatBytes } from "utilities/utilities";
import { useTranslation } from "react-i18next";
import { LinearProgress } from "@mui/material";

const UploadDropZone = ({
    onFilesChange,
    maxFiles = 1,
    acceptedFileTypes,
    hintText,
    mainText,
    renderUploadZone,
    hidePreview,
    dropzoneRef,
    hide,
    isUploading,
    progress = 0,
    showAddedFiles = true,
}: IDropZone) => {
    const classes = useStyles();

    const { acceptedFiles, getRootProps, getInputProps } = useDropzone({
        accept: acceptedFileTypes || {
            "image/png": [".png"],
            "image/jpeg": [".jpeg"],
            "image/jpg": [".jpg"],
        },
        noKeyboard: true,
        maxFiles,
        onDrop: (acceptedFiles) => {
            onFilesChange(acceptedFiles);
        },
    });

    const { t } = useTranslation();

    return (
        <section className={`${classes.container} ${hide ? classes.hideAll : ""}`}>
            {renderUploadZone && dropzoneRef ? (
                <Dropzone
                    ref={dropzoneRef}
                    noKeyboard
                    maxFiles={maxFiles}
                    accept={acceptedFileTypes}
                    onDrop={(acceptedFiles) => {
                        onFilesChange(acceptedFiles);
                    }}
                >
                    {({ getRootProps, getInputProps, acceptedFiles }) => {
                        return (
                            <>
                                <div {...getRootProps({ className: classes.dropzoneRenderArea })}>
                                    <input {...getInputProps()} />
                                    {renderUploadZone()}
                                </div>
                                {!!showAddedFiles && (
                                    <aside className={classes.fileList}>
                                        {acceptedFiles.length > 0 &&
                                            acceptedFiles.map((file) => (
                                                <div className={classes.fileListWrapper} key={file.name}>
                                                    <div className={classes.iconWrapper}>
                                                        <UploadFileIcon className={classes.icon} />
                                                    </div>
                                                    <div className={classes.fileDescription}>
                                                        <span className={classes.fileTitle}>{file.name}</span>
                                                        <span className={classes.fileSubtitle}>
                                                            {formatBytes(file.size)}
                                                        </span>
                                                    </div>
                                                    {isUploading && (
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={progress}
                                                            color="info"
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                    </aside>
                                )}
                                {!hidePreview && acceptedFiles.length > 0 && (
                                    <div className={classes.previewWrapper}>
                                        <div className={classes.titleContainer}>
                                            <h3 className={classes.formSubtitle}>{t("upload_dropzone.preview")}</h3>
                                        </div>
                                        <div className={classes.previewContainer}>
                                            {acceptedFiles.map((file) => (
                                                <img
                                                    key={file.name}
                                                    className={classes.preview}
                                                    src={URL.createObjectURL(file)}
                                                    alt={t("upload_dropzone.preview")}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </>
                        );
                    }}
                </Dropzone>
            ) : (
                <div {...getRootProps({ className: renderUploadZone ? classes.dropzoneRenderArea : classes.dropzone })}>
                    <input {...getInputProps()} />
                    {renderUploadZone ? (
                        renderUploadZone()
                    ) : (
                        <>
                            <div className={classes.iconWrapper}>
                                <UploadFileIcon className={classes.icon} />
                            </div>
                            <p className={classes.text}>
                                <span className={classes.spanText}>{t("upload_dropzone.click_to_select")}</span>{" "}
                                {mainText ? mainText : t("upload_dropzone.or_drag_and_drop")}
                                {hintText && <span className={classes.hintText}>{hintText}</span>}
                            </p>
                        </>
                    )}
                </div>
            )}
            {!!showAddedFiles && (
                <aside className={classes.fileList}>
                    {acceptedFiles.length > 0 &&
                        acceptedFiles.map((file) => (
                            <div className={classes.fileListWrapper} key={file.name}>
                                <div className={classes.iconWrapper}>
                                    <UploadFileIcon className={classes.icon} />
                                </div>
                                <div className={classes.fileDescription}>
                                    <span className={classes.fileTitle}>{file.name}</span>
                                    <span className={classes.fileSubtitle}>{formatBytes(file.size)}</span>
                                </div>
                            </div>
                        ))}
                </aside>
            )}
            {!hidePreview && acceptedFiles.length > 0 && (
                <div className={classes.previewWrapper}>
                    <div className={classes.titleContainer}>
                        <h3 className={classes.formSubtitle}>{t("upload_dropzone.preview")}</h3>
                    </div>
                    <div className={classes.previewContainer}>
                        {acceptedFiles.map((file) => (
                            <img
                                key={file.name}
                                className={classes.preview}
                                src={URL.createObjectURL(file)}
                                alt={t("upload_dropzone.preview")}
                            />
                        ))}
                    </div>
                </div>
            )}
        </section>
    );
};

export default UploadDropZone;
